import { fetchJSON as fetchAPI } from './client';

export type BusinessObjective = { key: string; name: string; description: string; kpi?: string };

export type WorkspacePhilosophyQuestion = { key: string; question: string; answered_by?: string };

export type LayoutRegion = { key: string; name: string; order: number; region: string; component?: string; description?: string };

export type NavigationTab = { key: string; name: string; route: string; icon?: string; description?: string; order: number };

export type DNASection = {
  key: string; name: string; current_state?: string; historical_trend?: string; benchmark?: string;
  ai_insights?: string[]; recommendations?: string[]; confidence?: number;
};

export type ExecutiveKPI = {
  key: string; name: string; metric?: string; unit?: string; drill_down?: string; alert_threshold?: string;
};

export type ProductRecommendation = {
  key: string; title: string; business_problem: string; evidence?: string[];
  expected_improvement: string; expected_revenue: string; expected_profit: string; confidence: number;
  implementation_time: string; experiment_link?: string; rollback_strategy?: string;
  business_owner?: string; ai_reasoning?: string; category?: string;
};

export type TimelineEventType = { key: string; name: string; icon?: string; description?: string };

export type KnowledgeGraphRelation = {
  key: string; name: string; source_type: string; target_type: string; description?: string;
};

export type AIWorkspaceSection = { key: string; name: string; description: string; persistent: boolean };

export type QuickAction = { key: string; name: string; command: string; shortcut?: string; permission?: string; target_latency?: string };

export type PerformanceTarget = { key: string; surface: string; max_value: string; unit: string; description?: string };

export type APISpecification = {
  key: string; category: string; endpoints?: string[]; description: string;
  authorization?: string; rate_limit?: string; cache_strategy?: string;
};

export type DatabaseTable = {
  key: string; name: string; purpose: string; columns?: string[]; indexes?: string[]; relationships?: string[];
};

export type TestingRequirement = { key: string; level: string; scope: string; required: boolean; description?: string };

export type AcceptanceCriterion = { key: string; criterion: string; measurable: boolean; target?: string };

export type WorkspaceValidation = {
  valid: boolean; layout_complete: boolean; navigation_complete: boolean; dna_panel_ready: boolean;
  recommendations_ready: boolean; performance_met: boolean; acceptance_passed: boolean;
  gaps?: string[]; evaluated_at: string;
};

export type WorkspaceDashboard = {
  prd_id: string; tenant_id: string; version: string; depends_on: string[];
  business_objectives: number; workspace_questions: number; layout_regions: number; navigation_tabs: number;
  dna_sections: number; executive_kpis: number; recommendations: number; timeline_event_types: number;
  knowledge_graph_relations: number; ai_workspace_sections: number; quick_actions: number;
  performance_targets: number; api_specifications: number; database_tables: number;
  testing_requirements: number; acceptance_criteria: number; generated_at: string;
};

export type ASIN360PRDStatus = {
  prd_id: string; version: string; depends_on: string[];
  business_objectives: number; workspace_questions: number; layout_regions: number; navigation_tabs: number;
  dna_sections: number; executive_kpis: number; recommendations: number; timeline_event_types: number;
  knowledge_graph_relations: number; ai_workspace_sections: number; quick_actions: number;
  performance_targets: number; api_specifications: number; database_tables: number;
  testing_requirements: number; acceptance_criteria: number;
  primary_business_entity: boolean; production_ready: boolean; status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const productWorkspaceASIN360Api = {
  dashboard: () => fetchMarketIntel<WorkspaceDashboard>('/asin360/dashboard'),
  businessObjectives: () => fetchMarketIntel<{ objectives: BusinessObjective[] }>('/asin360/business-objectives').then((r) => r.objectives),
  workspacePhilosophy: () => fetchMarketIntel<{ philosophy: WorkspacePhilosophyQuestion[] }>('/asin360/workspace-philosophy').then((r) => r.philosophy),
  layout: () => fetchMarketIntel<{ layout: LayoutRegion[] }>('/asin360/layout').then((r) => r.layout),
  navigationTabs: () => fetchMarketIntel<{ tabs: NavigationTab[] }>('/asin360/navigation-tabs').then((r) => r.tabs),
  dnaPanel: () => fetchMarketIntel<{ dna_sections: DNASection[] }>('/asin360/dna-panel').then((r) => r.dna_sections),
  executiveKPIs: () => fetchMarketIntel<{ kpis: ExecutiveKPI[] }>('/asin360/executive-kpis').then((r) => r.kpis),
  recommendations: () => fetchMarketIntel<{ recommendations: ProductRecommendation[] }>('/asin360/recommendations').then((r) => r.recommendations),
  timelineEvents: () => fetchMarketIntel<{ event_types: TimelineEventType[] }>('/asin360/timeline-events').then((r) => r.event_types),
  knowledgeGraph: () => fetchMarketIntel<{ relations: KnowledgeGraphRelation[] }>('/asin360/knowledge-graph').then((r) => r.relations),
  aiWorkspace: () => fetchMarketIntel<{ sections: AIWorkspaceSection[] }>('/asin360/ai-workspace').then((r) => r.sections),
  quickActions: () => fetchMarketIntel<{ actions: QuickAction[] }>('/asin360/quick-actions').then((r) => r.actions),
  performanceTargets: () => fetchMarketIntel<{ targets: PerformanceTarget[] }>('/asin360/performance-targets').then((r) => r.targets),
  apis: () => fetchMarketIntel<{ apis: APISpecification[] }>('/asin360/apis').then((r) => r.apis),
  databaseSchema: () => fetchMarketIntel<{ tables: DatabaseTable[] }>('/asin360/database-schema').then((r) => r.tables),
  testing: () => fetchMarketIntel<{ requirements: TestingRequirement[] }>('/asin360/testing').then((r) => r.requirements),
  acceptanceCriteria: () => fetchMarketIntel<{ criteria: AcceptanceCriterion[] }>('/asin360/acceptance-criteria').then((r) => r.criteria),
  validate: (payload: { attrs: Record<string, boolean> }) =>
    fetchMarketIntel<WorkspaceValidation>('/asin360/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchMarketIntel<ASIN360PRDStatus>('/asin360/status'),
};
